import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 60_000; // 60s per user preference
const LS_KEY = "smartech_app_version";
const LS_LAST_RELOAD_KEY = "smartech_last_version_reload_at";
// Circuit-breaker: refuse to auto-reload more than once per RELOAD_COOLDOWN_MS
// so that a multi-replica backend returning different versions per pod can't
// trap the user in an infinite reload loop.
const RELOAD_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Polls /api/version every 60s. When the server version differs from the one
 * the SPA was loaded with, force a full reload so the user always gets the
 * latest bundle right after a deployment.
 *
 * Preserves auth tokens so the user does not get logged out by the reload.
 */
export function useVersionCheck() {
  const reloadedRef = useRef(false);

  useEffect(() => {
    let timer = null;
    let aborted = false;

    const checkOnce = async () => {
      if (reloadedRef.current || aborted) return;
      try {
        const res = await axios.get(`${API}/version`, {
          // Bust any intermediate proxy cache
          params: { _t: Date.now() },
          headers: { "Cache-Control": "no-cache" },
          timeout: 8000,
        });
        const serverVersion = res?.data?.version;
        if (!serverVersion) return;
        const stored = localStorage.getItem(LS_KEY);
        if (!stored) {
          localStorage.setItem(LS_KEY, serverVersion);
          return;
        }
        if (stored !== serverVersion) {
          // Circuit-breaker — if we already reloaded recently, just refresh the
          // stored version silently and skip the reload. This prevents an
          // infinite reload loop when the backend runs multiple replicas that
          // return different versions per pod.
          const lastReloadAt = Number(localStorage.getItem(LS_LAST_RELOAD_KEY) || 0);
          if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) {
            localStorage.setItem(LS_KEY, serverVersion);
            return;
          }
          reloadedRef.current = true;
          // Persist the new version + reload timestamp so we don't loop.
          localStorage.setItem(LS_KEY, serverVersion);
          localStorage.setItem(LS_LAST_RELOAD_KEY, String(Date.now()));
          try {
            toast.success("Nouvelle version disponible — rechargement…", { duration: 2000 });
          } catch (e) { console.warn("[version-check] toast failed:", e); }
          // Unregister all service workers + wipe caches before reloading,
          // so the new HTML can pull fresh JS/CSS bundles.
          try {
            if ("serviceWorker" in navigator) {
              const regs = await navigator.serviceWorker.getRegistrations();
              await Promise.all(regs.map((r) => r.unregister()));
            }
          } catch (e) { console.warn("[version-check] SW unregister failed:", e); }
          try {
            if (window.caches) {
              const names = await window.caches.keys();
              await Promise.all(names.map((n) => window.caches.delete(n)));
            }
          } catch (e) { console.warn("[version-check] cache clear failed:", e); }
          // Tiny delay so the toast can render, then hard reload.
          setTimeout(() => {
            try { window.location.reload(); } catch (e) { console.warn("[version-check] reload failed:", e); }
          }, 800);
        }
      } catch (e) {
        // Network blip — silent retry next tick. Use debug to avoid noisy logs.
        if (process.env.NODE_ENV === "development") console.debug("[version-check] poll failed:", e?.message);
      }
    };

    // First check immediately, then every POLL_MS.
    checkOnce();
    timer = setInterval(checkOnce, POLL_MS);

    // Also re-check whenever the tab regains focus.
    const onFocus = () => checkOnce();
    window.addEventListener("focus", onFocus);

    return () => {
      aborted = true;
      if (timer) clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
    // API, LS_KEY, POLL_MS are module-level constants — stable for the
    // lifetime of the component, so this effect must run exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
